


import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance from "../../services/axiosInstance";

// 🔥 Get ALL announcements
export const fetchAnnouncements = createAsyncThunk(
  "announcements/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/api/announcements");
      if (res.data.status) return res.data.data;
      return rejectWithValue("Failed to load announcements");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Server error");
    }
  }
);

// 🔥 Get ACTIVE announcements
export const fetchActiveAnnouncements = createAsyncThunk(
  "announcements/fetchActive",
  async (_, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.get("/api/announcements/active");
      if (res.data.status) return res.data.data;
      return rejectWithValue("Failed to load active announcements");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Server error");
    }
  }
);

// 🔥 Create announcement
export const createAnnouncement = createAsyncThunk(
  "announcements/create",
  async (announcementData, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('title', announcementData.title);
      formData.append('description', announcementData.content);
      formData.append('createdBy', 'Admin');
      formData.append('department', announcementData.category || 'all');
      formData.append('priority', announcementData.priority || 'Medium');
      formData.append('status', announcementData.status === 'Active' ? 'active' : 'inactive');
      
      // Set expiry date (30 days from now)
      const expiryDate = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      formData.append('expiryDate', expiryDate.toISOString());
      
      if (announcementData.imageFile) {
        formData.append('image', announcementData.imageFile);
      }
      
      if (announcementData.attachments && announcementData.attachments.length > 0) {
        announcementData.attachments.forEach((file) => {
          formData.append('attachments', file);
        });
      }

      const res = await axiosInstance.post("/api/announcements", formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (res.data.status) return res.data.data;
      return rejectWithValue("Failed to create announcement");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Server error");
    }
  }
);

// 🔥 Update announcement
export const updateAnnouncement = createAsyncThunk(
  "announcements/update",
  async ({ id, announcementData }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append('title', announcementData.title);
      formData.append('description', announcementData.content);
      formData.append('status', announcementData.status === 'Active' ? 'active' : 'inactive');
      formData.append('department', announcementData.category || 'General');
      formData.append('priority', announcementData.priority || 'Medium');
      
      // Set expiry date if needed
      if (announcementData.date) {
        const expiryDate = new Date(announcementData.date);
        expiryDate.setDate(expiryDate.getDate() + 30); // Add 30 days
        formData.append('expiryDate', expiryDate.toISOString());
      }
      
      if (announcementData.imageFile) {
        formData.append('image', announcementData.imageFile);
      }
      
      const res = await axiosInstance.put(`/api/announcements/${id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (res.data.status) return res.data.data;
      return rejectWithValue("Failed to update announcement");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Server error");
    }
  }
);

// 🔥 Delete announcement
export const deleteAnnouncement = createAsyncThunk(
  "announcements/delete",
  async (id, { rejectWithValue }) => {
    try {
      const res = await axiosInstance.delete(`/api/announcements/${id}`);
      if (res.data.status) return id;
      return rejectWithValue("Failed to delete announcement");
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Server error");
    }
  }
);

const announcementSlice = createSlice({
  name: "announcements",
  initialState: {
    list: [],
    active: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ===== FETCH ALL =====
      .addCase(fetchAnnouncements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAnnouncements.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.map(item => ({
          id: item._id,
          title: item.title,
          content: item.description,
          date: new Date(item.createdAt).toISOString().split('T')[0],
          status: new Date(item.expiryDate) >= new Date() ? 'Active' : 'Inactive',
          image: item.imageUrl || "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
          category: item.department || "General",
          priority: item.priority || "Medium",
          views: item.views || 0,
          attachments: item.attachments || []
        }));
      })
      .addCase(fetchAnnouncements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== FETCH ACTIVE =====
      .addCase(fetchActiveAnnouncements.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchActiveAnnouncements.fulfilled, (state, action) => {
        state.loading = false;
        state.active = action.payload.map(item => ({
          id: item._id,
          title: item.title,
          content: item.description,
          date: new Date(item.createdAt).toISOString().split('T')[0],
          status: 'Active',
          image: item.imageUrl || "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
          category: item.department || "General",
          priority: item.priority || "Medium",
          views: item.views || 0
        }));
      })
      .addCase(fetchActiveAnnouncements.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== CREATE =====
      .addCase(createAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        const newAnnouncement = {
          id: action.payload._id,
          title: action.payload.title,
          content: action.payload.description,
          date: new Date(action.payload.createdAt).toISOString().split('T')[0],
          status: new Date(action.payload.expiryDate) >= new Date() ? 'Active' : 'Inactive',
          image: action.payload.imageUrl || "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
          category: action.payload.department || "General",
          priority: action.payload.priority || "Medium",
          views: 0,
          attachments: action.payload.attachments || []
        };
        state.list.unshift(newAnnouncement);
        if (newAnnouncement.status === 'Active') {
          state.active.unshift(newAnnouncement);
        }
      })
      .addCase(createAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== UPDATE =====
      .addCase(updateAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        const updated = {
          id: action.payload._id,
          title: action.payload.title,
          content: action.payload.description,
          date: new Date(action.payload.createdAt).toISOString().split('T')[0],
          status: action.payload.status === 'active' ? 'Active' : 'Inactive',
          image: action.payload.imageUrl || "https://images.unsplash.com/photo-1551288049-bebda4e38f71",
          category: action.payload.department || "General",
          priority: action.payload.priority || "Medium",
          views: action.payload.views || 0,
          attachments: action.payload.attachments || []
        };
        
        // Update in list
        const listIndex = state.list.findIndex(item => item.id === action.payload._id);
        if (listIndex !== -1) {
          state.list[listIndex] = updated;
        } else {
          state.list.unshift(updated);
        }
        
        // Update in active array
        if (updated.status === 'Active') {
          const activeIndex = state.active.findIndex(item => item.id === action.payload._id);
          if (activeIndex !== -1) {
            state.active[activeIndex] = updated;
          } else {
            state.active.unshift(updated);
          }
        } else {
          state.active = state.active.filter(item => item.id !== action.payload._id);
        }
      })
      .addCase(updateAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // ===== DELETE =====
      .addCase(deleteAnnouncement.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteAnnouncement.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(item => item.id !== action.payload);
        state.active = state.active.filter(item => item.id !== action.payload);
      })
      .addCase(deleteAnnouncement.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError } = announcementSlice.actions;
export default announcementSlice.reducer;